import React, { useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';

import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from '../modal/styles';
import { TimesIcon } from '../icon/solid';
import FormSubmit from './actions/FormSubmit';
import FormReset from './actions/FormReset';
import { useId } from '../../hooks';
import { ReactHookFormModalProps } from './types';
import { selectIsNeedCloseModal } from '../../store/modules/modal/selectors';
import { setNeedCloseModal } from '../../store/modules/modal/actions';

const ReactHookFormModal = ({
  open,
  formSettings,
  title,
  children,
  onCloseHandler,
}: ReactHookFormModalProps) => {
  const id = useId();
  const classes = useStyles();
  const isNeedCloseModal = useSelector(selectIsNeedCloseModal);
  const dispatch = useDispatch();

  const {
    validationSchema,
    initialValues,
    creatingMode,
    submitTitle,
    onSubmit,
    loading,
    hideCancel,
  } = formSettings;

  // Form Setup
  const form = useForm({
    defaultValues: initialValues || {},
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    shouldUnregister: false,
  });

  const { reset, handleSubmit, formState: { isValid } } = form;

  const submitHandler = useCallback((data: any) => {
    onSubmit(data);
  }, [onSubmit]);

  useEffect(() => {
    if (isNeedCloseModal) {
      dispatch(setNeedCloseModal(false));
      onCloseHandler();
    }
  }, [dispatch, isNeedCloseModal, onCloseHandler]);

  return (
    <Dialog
      open={open}
      onEscapeKeyDown={onCloseHandler}
      aria-labelledby={id}
      disableBackdropClick
      PaperProps={{ className: classes.modal }}
      fullWidth
      style={{ zIndex: 2147483100 }}
    >
      <DialogTitle id={id}>
        {title}
        <IconButton aria-label="close" className={classes.closeButton} onClick={onCloseHandler}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <FormProvider {...form}>
          <form>
            {children}
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions className={clsx(classes.dialogActions, {
        [classes.reactFormDialogActions]: hideCancel,
        })}>
        {!hideCancel && (
          <FormReset
            className={clsx(classes.cancelButton, classes.panelButton)}
            variant="contained"
            color="default"
            withHotkey
            closeDialog={onCloseHandler}
            resetHandler={reset}
          >
            <TimesIcon className={classes.buttonIcon} />
            <span className={classes.buttonText}>Cancel</span>
          </FormReset>
        )}
        <FormSubmit
          submitHandler={handleSubmit((d) => submitHandler(d))}
          className={classes.panelButton}
          variant="contained"
          color="primary"
          disabled={!isValid || loading}
        >
          <span className={classes.buttonText}>
            {submitTitle || (creatingMode ? 'Create' : 'Save')}
          </span>
        </FormSubmit>
      </DialogActions>
    </Dialog>
  );
};

export default ReactHookFormModal;
